import useCohortPermissions from "@pathwright/web/src/modules/cohort/useCohortPermissions"
import { getStoredPathMode } from "@pathwright/web/src/modules/path/utils"
import { useQuery } from "@pathwright/web/src/modules/utils/apollo"
import gql from "graphql-tag"
import flowRight from "lodash/flowRight"
import get from "lodash/get"
import { LEARN } from "path/constants"
import { useContext, useEffect } from "react"
import "./ResourcePath.css"
import { PathContext } from "./state/PathProvider"
import { usePathParamIds } from "./state/utils"
import withAvailableModes from "./withAvailableModes"

const USER_PATH_ID_QUERY = gql`
  query UserPathIdQuery($cohortId: Int!) {
    group(id: $cohortId) {
      id
      user_registration {
        id
        path {
          id
        }
      }
    }
  }
`

const withResourcePath = (Component) => {
  function ComponentWithResourcePath({ children, availableModes, ...rest }) {
    const { pathId, cohortId } = usePathParamIds(rest)
    const storedPathMode = getStoredPathMode()
    const cohortPerms = useCohortPermissions({ cohortId })

    const {
      path,
      loaded,
      loading,
      syncing,
      loadPath,
      pathId: currentPathId,
      cohortId: currentCohortId
    } = useContext(PathContext)

    // either use pathId from query params, currentPathId if the cohort hasn't changed, or null for pathId
    let queriedPathId =
      pathId || (currentCohortId === cohortId ? currentPathId : null)

    // now, depending on the storedPathMode, we may need to clear or set the queriedPathId
    // that is, we do not want to explicitly load a user's path when not in LEARN mode,
    // nor do we want to implicitly load a user's path when in LEARN mode
    const userPathIdQuery = useQuery(USER_PATH_ID_QUERY, {
      variables: {
        cohortId
      },
      skip:
        !cohortPerms.hasTeacherLevelAccess ||
        storedPathMode !== LEARN ||
        // Ensure user actually has access to the storedPathMode for this cohort.
        !availableModes.includes(storedPathMode) ||
        !!queriedPathId
    })

    const userPathId = get(
      userPathIdQuery,
      "data.group.user_registration.path.id"
    )

    // potentially updating the queriedPathId, in the case where a user has teacher level access,
    // with a storedPathMode of LEARN and no set queriedPathId
    queriedPathId = queriedPathId || userPathId || null

    // clearing the queriedPathId
    if (storedPathMode !== LEARN && queriedPathId) {
      queriedPathId = null
    }

    // bool for whether dependent queries are still loading
    const establishingQueriedPathId =
      userPathIdQuery.loading || cohortPerms.hasTeacherLevelAccess === null

    // setting path related ids based on route props
    useEffect(() => {
      if (!establishingQueriedPathId) {
        loadPath({
          // either use pathId from query params, currentPathId if the cohort hasn't changed, or null for pathId
          pathId: queriedPathId,
          cohortId
        })
      }
    }, [pathId, cohortId, establishingQueriedPathId])

    return (
      <Component
        pathProviderPath={path} // `path` prop is from the Backbone store; this is from GQL
        loaded={loaded}
        loading={loading}
        syncing={syncing}
        pathId={queriedPathId}
        cohortId={cohortId}
        {...rest}
      >
        {children}
      </Component>
    )
  }

  ComponentWithResourcePath.displayName = `${
    Component.displayName || "Component"
  }WithResourcePath`

  return ComponentWithResourcePath
}

export default flowRight(withAvailableModes, withResourcePath)
